/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useClientAttendanceNotice } from '@features/client-management/hooks/useClientAttendanceNotice';
import { useClientOpenAttendance } from '@features/client-management/hooks/useClientOpenAttendance';
import { brokerBreakdown, totalOf } from '@features/client-management/utils/broker-breakdown';

export interface ClientOpenAttendanceWarningProps {
  agencyId: string;
  /** The client picked in the form; empty renders nothing. */
  clientId: string | null;
}

/**
 * Warns, before the attendance is opened, that this client already has one.
 *
 * Deliberately wider than the peer notice on the client profile: that one
 * answers "is a *colleague* working this client", and so stays silent on
 * your own. Here your own is exactly the case worth catching — opening a
 * second attendance for a client you are already attending is the
 * duplicate a broker makes by accident, and nothing downstream merges
 * the two afterwards.
 *
 * Two sources because neither sees everything on its own. The listing is
 * scoped by `view_all_deals`, so for a corretor it returns their own and
 * nothing else; the notice endpoint is unscoped but reports only *other*
 * brokers. Together they cover both sides of the collision for every
 * role.
 *
 * It informs and does not block: an agency may legitimately run two
 * attendances for one person — a purchase and a rental at once — and a
 * dialog that refused would make that impossible instead of deliberate.
 *
 * @param props.agencyId - The owning agency.
 * @param props.clientId - The client picked in the form.
 * @returns The warning strip, or null when this client is free.
 */
export function ClientOpenAttendanceWarning({
  agencyId, clientId,
}: ClientOpenAttendanceWarningProps) {
  const { t } = useTranslation('features/deals');
  const members = useAgency((s) => s.agency?.members) ?? [];
  const { openAttendances } = useClientOpenAttendance(agencyId, clientId ?? '');
  const peer = useClientAttendanceNotice(agencyId, clientId ?? '');

  if (!clientId) return null;

  // Merged per broker rather than per source. The notice endpoint
  // already counts every *other* broker, so the listing only contributes
  // the brokers it does not cover -- in practice the reader's own, the
  // one side that endpoint is blind to. Counting a broker from both
  // would double them.
  const own: Record<string, number> = {};
  openAttendances.forEach((deal) => {
    const broker = (deal.broker_id ?? '').toLowerCase();
    if (!broker || peer.brokerCounts[broker]) return;
    own[broker] = (own[broker] ?? 0) + 1;
  });
  const counts = { ...peer.brokerCounts, ...own };

  const count = totalOf(counts);
  if (count === 0) return null;

  const brokers = brokerBreakdown(members, counts);

  return (
    <div
      data-testid="create-deal-open-attendance-warning"
      className={cn(
        'flex items-start gap-(--space-2) rounded-(--radius-md)',
        'border border-[color-mix(in_srgb,var(--status-warning)_35%,transparent)]',
        'bg-[color-mix(in_srgb,var(--status-warning)_10%,var(--surface-primary))]',
        'px-(--space-3) py-(--space-2)',
      )}
    >
      <ExclamationTriangleIcon
        width={14}
        height={14}
        className="mt-(--space-0-5) shrink-0 text-(--status-warning-text)"
      />
      <Text component="span" variant="bodySm" tone="secondary">
        {brokers
          ? t('form.clientHasOpenAttendanceWith', { count, brokers })
          : t('form.clientHasOpenAttendance', { count })}
      </Text>
    </div>
  );
}

export default ClientOpenAttendanceWarning;
